import { BowChemistry, ChemistryEdit, ChemistryRecord, SanitizerTypes } from "../../documents/Chemistry";
import { safeJsonParse } from "../../../utils/Json";
import logger from "../../../utils/Logger";
import { isSet } from "../../../utils";
import * as GlobalTypes from "../../../types";


/**
 * Manipulates passed in config in place.
 * Only add migration here that is independent from siteStates (ChemistryEdits)
 */
export default function migrateChemistryConfig(config: ChemistryRecord) {
    if (!config) {
        logger.info("[config.migration.chemistry] migrateChemistryConfig() Empty config was passed in. Skip migration.");
        return;
    }

    if (!config.data) {
        logger.info("[config.migration.chemistry] migrateChemistryConfig() Config data was empty. Creating it...");
        config.data = [];
    }

    config.data.forEach(item => {
        if (!item.SanitizerTarget && isSet(item.ORPTarget)) {
            item.SanitizerTarget = {
                Target: item.ORPTarget,
                Type: SanitizerTypes.ORP
            };
        }
        delete item.ORPTarget;

        if (!item.SanitizerAlertTooLow && isSet(item.ORPAlertTooLow)) {
            item.SanitizerAlertTooLow = {
                Target: item.ORPAlertTooLow,
                Type: SanitizerTypes.ORP
            };
        }
        delete item.ORPAlertTooLow;

        if (!item.SanitizerAlertTooHigh && isSet(item.ORPAlertTooHigh)) {
            item.SanitizerAlertTooHigh = {
                Target: item.ORPAlertTooHigh,
                Type: SanitizerTypes.ORP
            };
        }
        delete item.ORPAlertTooHigh;

        if (!isSet(item.SanitizerAlertEnabled) && isSet(item.ORPAlertEnabled)) {
            item.SanitizerAlertEnabled = item.ORPAlertEnabled;
        }
        delete item.ORPAlertEnabled;
        
        if (!isSet(item.SanitizerAlertDelayMinutes) && isSet(item.ORPAlertDelayMinutes)) {
            item.SanitizerAlertDelayMinutes = item.ORPAlertDelayMinutes;
        }
        delete item.ORPAlertDelayMinutes;

    });

    logger.debug("[config.migration.chemistry] migrateChemistryConfig() Migrated Chemistry config:", config);
}

/**
 * Manipulates passed in config in place.
 */
export function migrateChemistryConfigUsingState(config: ChemistryRecord, siteStates) {
    const autoFilterVersion = Number(siteStates?.AutoFilterVersion);
    const chemistryEditsRaw = siteStates?.ChemistryEdits;
    const chemistryEdits = (safeJsonParse(chemistryEditsRaw || "[]") || []) as ChemistryEdit[];

    if (!config.data) {
        logger.info("[config.migration.chemistry] migrateChemistryConfigUsingState() Config data was empty. Creating it...");
        config.data = [];
    }

    chemistryEdits.forEach(itemBowChemistry => {
        if (!config.data.some(item => item.BodyOfWaterUUID === itemBowChemistry.BodyOfWaterUUID)) {
            logger.info("[config.migration.chemistry] migrateChemistryConfigUsingState() BOW exists in ChemistryEdits but not in Chemistry Config. Creating it. BOW:", itemBowChemistry.BodyOfWaterUUID);
            config.data.push({
                BodyOfWaterUUID: itemBowChemistry.BodyOfWaterUUID as GlobalTypes.UUIDV4,
            } as BowChemistry);
        }
    });

    config.data.forEach(itemBowChemistry => {
        const chemistryEdit = chemistryEdits.find(item => item.BodyOfWaterUUID === itemBowChemistry.BodyOfWaterUUID);
        logger.debug("[config.migration.chemistry] migrateChemistryConfigUsingState() Migrating BOW chemistry config:", itemBowChemistry, "using chemistryEdit:", chemistryEdit);

        // Fix when manually edited config is not ordered
        if (itemBowChemistry?.AcidDosingProcedure?.DosingRows?.length) {
            itemBowChemistry.AcidDosingProcedure.DosingRows.sort((a, b) => (a.FromValue < b.FromValue ? -1 : 1));
        }
        if (itemBowChemistry?.BaseDosingProcedure?.DosingRows?.length) {
            itemBowChemistry.BaseDosingProcedure.DosingRows.sort((a, b) => (a.FromValue < b.FromValue ? -1 : 1));
        }
        if (itemBowChemistry?.CO2DosingProcedure?.DosingRows?.length) {
            itemBowChemistry.CO2DosingProcedure.DosingRows.sort((a, b) => (a.FromValue < b.FromValue ? -1 : 1));
        }
        if (itemBowChemistry?.LiquidChlorineDosingProcedure?.DosingRows?.length) {
            itemBowChemistry.LiquidChlorineDosingProcedure.DosingRows.sort((a, b) => (a.FromValue < b.FromValue ? -1 : 1));
        }
        if (itemBowChemistry?.SaltChlorineDosing?.NotCovered?.DosingRows?.length) {
            itemBowChemistry.SaltChlorineDosing.NotCovered.DosingRows.sort((a, b) => (a.FromValue < b.FromValue ? -1 : 1));
        }
        if (itemBowChemistry?.SaltChlorineDosing?.Covered?.DosingRows?.length) {
            itemBowChemistry.SaltChlorineDosing.Covered.DosingRows.sort((a, b) => (a.FromValue < b.FromValue ? -1 : 1));
        }

        if (chemistryEdit) {
            if (!isSet(itemBowChemistry.DispensingAcidEnabled)) {
                itemBowChemistry.DispensingAcidEnabled = chemistryEdit.AcidOnByDefault;
            }
            if (!isSet(itemBowChemistry.AcidDosingProcedureUseDefault) && autoFilterVersion >= 34 && itemBowChemistry.DispensingAcidEnabled) {
                if (itemBowChemistry.AcidDosingProcedure) {
                    itemBowChemistry.AcidDosingProcedureUseDefault = false;
                } else {
                    itemBowChemistry.AcidDosingProcedureUseDefault = true;
                    itemBowChemistry.AcidDosingProcedure = null;
                }
            }

            if (!isSet(itemBowChemistry.DispensingBaseEnabled)) {
                itemBowChemistry.DispensingBaseEnabled = chemistryEdit.BaseOnByDefault;
            }
            if (!isSet(itemBowChemistry.BaseDosingProcedureUseDefault) && autoFilterVersion >= 34 && itemBowChemistry.DispensingBaseEnabled) {
                if (itemBowChemistry.BaseDosingProcedure) {
                    itemBowChemistry.BaseDosingProcedureUseDefault = false;
                } else {
                    itemBowChemistry.BaseDosingProcedureUseDefault = true;
                    itemBowChemistry.BaseDosingProcedure = null;
                }
            }

            if (!isSet(itemBowChemistry.DispensingCO2Enabled)) {
                itemBowChemistry.DispensingCO2Enabled = chemistryEdit.CO2OnByDefault;
            }
            if (!isSet(itemBowChemistry.CO2DosingProcedureUseDefault) && autoFilterVersion >= 34 && itemBowChemistry.DispensingCO2Enabled) {
                if (itemBowChemistry.CO2DosingProcedure) {
                    itemBowChemistry.CO2DosingProcedureUseDefault = false;
                } else {
                    itemBowChemistry.CO2DosingProcedureUseDefault = true;
                    itemBowChemistry.CO2DosingProcedure = null;
                }
            }

            if (!isSet(itemBowChemistry.DispensingLiquidChlorineEnabled)) {
                itemBowChemistry.DispensingLiquidChlorineEnabled = chemistryEdit.LiquidChlorineOnByDefault;
            }
            if (!isSet(itemBowChemistry.LiquidChlorineDosingProcedureUseDefault) && autoFilterVersion >= 34 && itemBowChemistry.DispensingLiquidChlorineEnabled) {
                if (itemBowChemistry.LiquidChlorineDosingProcedure) {
                    itemBowChemistry.LiquidChlorineDosingProcedureUseDefault = false;
                } else {
                    itemBowChemistry.LiquidChlorineDosingProcedureUseDefault = true;
                    itemBowChemistry.LiquidChlorineDosingProcedure = null;
                }
            }

            if (!isSet(itemBowChemistry.DispensingSaltChlorineEnabled)) {
                itemBowChemistry.DispensingSaltChlorineEnabled = chemistryEdit.SaltChlorineOnByDefault;
            }
            if (!isSet(itemBowChemistry.SaltChlorineDosing?.NotCovered?.DosingRowsUseDefault) && autoFilterVersion >= 34 &&
                itemBowChemistry.DispensingSaltChlorineEnabled &&
                itemBowChemistry.SaltChlorineDosing?.NotCovered?.SaltChlorinatorDispenseMethod &&
                itemBowChemistry.SaltChlorineDosing.NotCovered.SaltChlorinatorDispenseMethod !== GlobalTypes.SaltChlorineDispenseMethods.MANUAL_PERCENT
            ) {
                if (itemBowChemistry.SaltChlorineDosing.NotCovered.DosingRows) {
                    itemBowChemistry.SaltChlorineDosing.NotCovered.DosingRowsUseDefault = false;
                } else {
                    itemBowChemistry.SaltChlorineDosing.NotCovered.DosingRowsUseDefault = true;
                    itemBowChemistry.SaltChlorineDosing.NotCovered.DosingRows = null;
                }
            
                if (itemBowChemistry.SaltChlorineDosing.CoveredDosingProcedureEnabled &&
                    itemBowChemistry.SaltChlorineDosing.Covered?.SaltChlorinatorDispenseMethod &&
                    itemBowChemistry.SaltChlorineDosing.Covered.SaltChlorinatorDispenseMethod !== GlobalTypes.SaltChlorineDispenseMethods.MANUAL_PERCENT
                ) {
                    if (itemBowChemistry.SaltChlorineDosing.Covered.DosingRows) {
                        itemBowChemistry.SaltChlorineDosing.Covered.DosingRowsUseDefault = false;
                    } else {
                        itemBowChemistry.SaltChlorineDosing.Covered.DosingRowsUseDefault = true;
                        itemBowChemistry.SaltChlorineDosing.Covered.DosingRows = null;
                    }
                }
            }

            /* Applying defaults is not complete yet, not adding it yet
            if (!isSet(itemBowChemistry.SaltChlorineDosing?.NotCovered?.ProbeDelayMinutes) &&
                Number(siteStates?.ChemistryVersion) >= 5 &&
                itemBowChemistry.DispensingSaltChlorineEnabled
            ) {
                itemBowChemistry.SaltChlorineDosing = {
                    ...itemBowChemistry.SaltChlorineDosing,
                    NotCovered: {
                        ...itemBowChemistry.SaltChlorineDosing?.NotCovered,
                        ProbeDelayMinutes: chemistryEdit.DefaultSaltDosingProcedure?.NotCovered?.ProbeDelayMinutes
                    }
                };
            }

            if (!isSet(itemBowChemistry.SaltChlorineDosing?.NotCovered?.SaltChlorinatorDispenseMethod) &&
                Number(siteStates?.ChemistryVersion) >= 5 &&
                itemBowChemistry.DispensingSaltChlorineEnabled
            ) {
                itemBowChemistry.SaltChlorineDosing = {
                    ...itemBowChemistry.SaltChlorineDosing,
                    NotCovered: {
                        ...itemBowChemistry.SaltChlorineDosing?.NotCovered,
                        SaltChlorinatorDispenseMethod: chemistryEdit.DefaultSaltDosingProcedure?.NotCovered?.SaltChlorinatorDispenseMethod
                    }
                };
            }
            */

            if (!isSet(itemBowChemistry.SanitizerAlertDelayMinutes)) {
                itemBowChemistry.SanitizerAlertDelayMinutes = autoFilterVersion >= 32 ?
                    chemistryEdit.DefaultSanitizerAlertDelayMinutes :
                    chemistryEdit.DefaultORPAlertDelayMinutes
                ;
            }
            if (!isSet(itemBowChemistry.SanitizerAlertEnabled)) {
                itemBowChemistry.SanitizerAlertEnabled = autoFilterVersion >= 32 ?
                    chemistryEdit.DefaultSanitizerAlertEnabled :
                    chemistryEdit.DefaultORPAlertEnabled
                ;
            }
            if (!isSet(itemBowChemistry.SanitizerAlertTooHigh)) {
                if (autoFilterVersion >= 32) {
                    // NOTE: there is no DefaultSanitizerAlertTyoe in ChemistryEdits
                    const Type = chemistryEdit.AvailableSanitizerTargetTypes && chemistryEdit.AvailableSanitizerTargetTypes[ 0 ];
                    const Target = getDefaultSanitizerAlertTooHighTargetByType(Type, chemistryEdit);

                    // Only set SanitizerAlertTooHigh if there are both a Target and a Type
                    if (Type && isSet(Target)) {
                        itemBowChemistry.SanitizerAlertTooHigh = {
                            Target,
                            Type,
                        };
                    }
                } else {
                    itemBowChemistry.SanitizerAlertTooHigh = {
                        Target: chemistryEdit.DefaultORPAlertTooHigh,
                        Type: SanitizerTypes.ORP
                    };
                }
            }
            if (!isSet(itemBowChemistry.SanitizerAlertTooLow)) {
                if (autoFilterVersion >= 32) {
                    // NOTE: there is no DefaultSanitizerAlertTyoe in ChemistryEdits
                    const Type = chemistryEdit.AvailableSanitizerTargetTypes && chemistryEdit.AvailableSanitizerTargetTypes[ 0 ];
                    const Target = getDefaultSanitizerAlertTooLowTargetByType(Type, chemistryEdit);
                    
                    // Only set SanitizerAlertTooLow if there are both a Target and a Type
                    if (Type && isSet(Target)) {
                        itemBowChemistry.SanitizerAlertTooLow = {
                            Target,
                            Type,
                        };
                    }
                } else {
                    itemBowChemistry.SanitizerAlertTooLow = {
                        Target: chemistryEdit.DefaultORPAlertTooLow,
                        Type: SanitizerTypes.ORP
                    };
                }
            }
            if (!isSet(itemBowChemistry.SanitizerTarget)) {
                if (autoFilterVersion >= 32) {
                    // NOTE: there is no DefaultSanitizerAlertTyoe in ChemistryEdits
                    const Type = chemistryEdit.AvailableSanitizerTargetTypes && chemistryEdit.AvailableSanitizerTargetTypes[ 0 ];
                    const Target = getDefaultSanitizerTargetByType(Type, chemistryEdit);

                    // Only set SanitizerAlertTooLow if there are both a Target and a Type
                    if (Type && isSet(Target)) {
                        itemBowChemistry.SanitizerTarget = {
                            Target,
                            Type,
                        };
                    }
                } else {
                    itemBowChemistry.SanitizerTarget = {
                        Target: chemistryEdit.DefaultORPTarget,
                        Type: SanitizerTypes.ORP
                    };
                }
            }
            if (!isSet(itemBowChemistry.OzoneEnabled)) {
                itemBowChemistry.OzoneEnabled = chemistryEdit.OzoneOnByDefault;
            }
            if (!isSet(itemBowChemistry.PHAlertDelayMinutes)) {
                itemBowChemistry.PHAlertDelayMinutes = chemistryEdit.DefaultPHAlertDelayMinutes;
            }
            if (!isSet(itemBowChemistry.PHAlertEnabled)) {
                itemBowChemistry.PHAlertEnabled = chemistryEdit.DefaultPHAlertEnabled;
            }
            if (!isSet(itemBowChemistry.PHAlertTooHigh)) {
                itemBowChemistry.PHAlertTooHigh = chemistryEdit.DefaultPHAlertTooHigh;
            }
            if (!isSet(itemBowChemistry.PHAlertTooLow)) {
                itemBowChemistry.PHAlertTooLow = chemistryEdit.DefaultPHAlertTooLow;
            }
            if (!isSet(itemBowChemistry.PHTarget)) {
                itemBowChemistry.PHTarget = chemistryEdit.DefaultPHTarget;
            }
            if (!isSet(itemBowChemistry.UvSanitizerEnabled)) {
                itemBowChemistry.UvSanitizerEnabled = chemistryEdit.UvSanitizerOnByDefault;
            }
        }
    });

    logger.debug("[config.migration.chemistry] migrateChemistryConfigUsingState() Migrated Chemistry config:", config);
}

export function getDefaultSanitizerAlertTooHighTargetByType(type: SanitizerTypes, chemistryEdit: ChemistryEdit): number {
switch (type) {
    case SanitizerTypes.FREE_CHLORINE:
        return chemistryEdit.DefaultFreeChlorineAlertTooHigh;
    case SanitizerTypes.TOTAL_CHLORINE:
        return chemistryEdit.DefaultTotalChlorineAlertTooHigh;
    case SanitizerTypes.ORP:
        return chemistryEdit.DefaultORPAlertTooHigh;
    }
}

export function getDefaultSanitizerAlertTooLowTargetByType(type: SanitizerTypes, chemistryEdit: ChemistryEdit): number {
    switch (type) {
    case SanitizerTypes.FREE_CHLORINE:
        return chemistryEdit.DefaultFreeChlorineAlertTooLow;
    case SanitizerTypes.TOTAL_CHLORINE:
        return chemistryEdit.DefaultTotalChlorineAlertTooLow;
    case SanitizerTypes.ORP:
        return chemistryEdit.DefaultORPAlertTooLow;;
    }
}

export function getDefaultSanitizerTargetByType(type: SanitizerTypes, chemistryEdit: ChemistryEdit): number {
    switch (type) {
    case SanitizerTypes.FREE_CHLORINE:
        return chemistryEdit.DefaultFreeChlorineTarget;
    case SanitizerTypes.TOTAL_CHLORINE:
        return chemistryEdit.DefaultTotalChlorineTarget;
    case SanitizerTypes.ORP:
        return chemistryEdit.DefaultORPTarget;
    }
}

/**
 * Converts passed in config in place to make it compatible with the passed in AutoFilterVersion
 * @param config 
 * @param autoFilterVersion Target AutoFilterVersion of the conversion. 
 */
export function getChemistryConfigBackwardCompatible(config: ChemistryRecord, autoFilterVersion: number, chemmistryEdits: ChemistryEdit[]) {
    if (autoFilterVersion < 17) {
        // Controller only supports integers
        logger.info('[config.migration.chemistry] getChemistryConfigBackwardCompatible() rounding MaximumDosingAmountPerDay and OverFeedAmount because of controller AutoFilterVersion:', autoFilterVersion);
        config.data.forEach(itemBody => {
            [ 'AcidDosingProcedure', 'BaseDosingProcedure', 'CO2DosingProcedure', 'LiquidChlorineDosingProcedure' ].forEach(item => {
                if (typeof itemBody[item]?.MaximumDosingAmountPerDay !== 'undefined') {
                    itemBody[item].MaximumDosingAmountPerDay = Math.round(itemBody[item].MaximumDosingAmountPerDay);
                }
                if (typeof itemBody[item]?.ErrorMethod?.OverFeedAmount !== 'undefined') {
                    itemBody[item].ErrorMethod.OverFeedAmount = Math.round(itemBody[item].ErrorMethod.OverFeedAmount);
                }
            });
        });
    }

    if (autoFilterVersion < 32) {
        config.data.forEach(item => {
            const chemistryEdit = chemmistryEdits.find(item => item.BodyOfWaterUUID === item.BodyOfWaterUUID);

            if (item.SanitizerAlertTooLow?.Type === SanitizerTypes.ORP) {
                item.ORPAlertTooLow = item.SanitizerAlertTooLow.Target;
            } else {
                item.ORPAlertTooLow = chemistryEdit?.DefaultORPAlertTooLow;
            }
            delete item.SanitizerAlertTooLow;

            if (item.SanitizerAlertTooHigh?.Type === SanitizerTypes.ORP) {
                item.ORPAlertTooHigh = item.SanitizerAlertTooHigh.Target;
            } else {
                item.ORPAlertTooHigh = chemistryEdit?.DefaultORPAlertTooHigh;
            }
            delete item.SanitizerAlertTooHigh;

            if (item.SanitizerTarget?.Type === SanitizerTypes.ORP) {
                item.ORPAlertEnabled = item.SanitizerAlertEnabled;
            } else {
                item.ORPAlertEnabled = chemistryEdit?.DefaultORPAlertEnabled;
            }
            delete item.SanitizerAlertEnabled;

            if (item.SanitizerTarget?.Type === SanitizerTypes.ORP) {
                item.ORPAlertDelayMinutes = item.SanitizerAlertDelayMinutes;
            } else {
                item.ORPAlertDelayMinutes = chemistryEdit?.DefaultORPAlertDelayMinutes;
            }
            delete item.SanitizerAlertDelayMinutes;

            if (item.SanitizerTarget?.Type === SanitizerTypes.ORP) {
                item.ORPTarget = item.SanitizerTarget.Target;
            } else {
                item.ORPTarget = chemistryEdit?.DefaultORPTarget;
            }
            delete item.SanitizerTarget;
            
        });
    }


    
}
